import * as React from 'react';
import styled from '@emotion/styled';
import { FormikContext, FormikProps } from 'formik';

import { RoleSelectWidget } from './RoleSelectWidget';
import { FormLayoutSection, FormLayoutWidget } from '../../../../widgets/layout/FormLayoutWidget';
import { FormikInputWidget } from '../../../../widgets/inputs/FormikInput';
import { useOrgStore } from '../../../../hooks/useOrgStore';
import { AbstractDeveloperModel, EditDeveloperValues } from './AbstractEditDeveloperDialogWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../../widgets/layout/CenterFormWidget';
import { GroupedButtons } from '../../../../widgets/buttons/GroupedButtons';
import { ButtonType } from '../../../../widgets/buttons/ButtonWidget';
import { useAllowed } from '../../../../hooks/useAllowed';
import { UserModelAction } from '@journeyapps-platform/cardinal-catalog';

export enum CreateUserFields {
  EMAIL = 'Email',
  ROLE = 'Roles'
}

export interface EditDevFormProps {
  developer: AbstractDeveloperModel | null;
  org_id: string;
  formik: FormikProps<EditDeveloperValues>;
  loading: boolean;
  onCancel?: () => any;
  onSubmit?: () => any;
}

const getButtons = (params: {
  developer: AbstractDeveloperModel | null;
  formik: FormikProps<EditDeveloperValues>;
  loading: boolean;
  onCancel?: () => any;
  onSubmit?: () => any;
}): FormLayoutSection => {
  const { developer, formik, loading, onCancel, onSubmit } = params;
  const submitDisabled = !formik.values.email;

  return {
    key: 'buttons',
    elements: [
      <GroupedButtons
        btns={[
          {
            label: 'Cancel',
            type: ButtonType.SECONDARY,
            action: (event) => {
              onCancel?.();
            }
          },
          {
            label: !!developer ? 'Update' : 'Add',
            disabled: submitDisabled,
            loadingOverride: loading,
            action: (event, loading) => {
              loading(true);
              formik
                .submitForm()
                .then(onSubmit)
                .finally(() => {
                  loading(false);
                });
            }
          }
        ]}
      />
    ]
  };
};

const getFormContent = (params: { developer: AbstractDeveloperModel | null; initialRoleIds: string[] }) => {
  const { developer, initialRoleIds } = params;

  const isAllowedToAssign = useAllowed({ action: UserModelAction.MANAGE_ASSIGNMENTS, resource: developer });

  return {
    key: 'content',
    elements: [
      <S.Container key="container" />, // Set min width
      <S.StyledInput
        key="email"
        name={'email'}
        label={CreateUserFields.EMAIL}
        input={{
          disabled: !!developer,
          placeholder: `Enter ${CreateUserFields.EMAIL.toLowerCase()}`
        }}
      />,
      <RoleSelectWidget
        key="roles"
        name={'roleIds'}
        label={CreateUserFields.ROLE}
        placeHolder={'Roles'}
        defaultValue={initialRoleIds}
        disabled={!isAllowedToAssign}
      />
    ]
  };
};

export const EditDevFormWidget: React.FC<EditDevFormProps> = (props) => {
  const orgStore = useOrgStore();

  const { formik, developer, org_id, loading, onCancel, onSubmit } = props;

  return (
    <FormikContext.Provider value={formik}>
      <CenterFormWidget
        noLogo={true}
        primaryLabel={!!developer ? 'Edit developer' : 'Add developer'}
        secondaryLabel={
          !!developer ? `Edit developer details` : `Add a developer to: ${orgStore.org(org_id).model.label}`
        }
        orientation={CenterFormOrientation.ROW}
      >
        <FormLayoutWidget
          sections={[
            getFormContent({ initialRoleIds: formik.values.roleIds, developer }),
            getButtons({ formik, loading, onCancel, onSubmit, developer })
          ]}
        />
      </CenterFormWidget>
    </FormikContext.Provider>
  );
};

const BASE_LABEL = 'create-dev-form';

namespace S {
  export const Container = styled.div`
    label: ${BASE_LABEL};
    display: flex;
    flex-direction: column;
    min-width: 440px;
  `;

  export const StyledInput = styled(FormikInputWidget)`
    label: ${BASE_LABEL}__input;
  `;
}
